/**
 * change-profile Component
 *
 *
 */

import { useState, useEffect } from 'react';
import OTPInput from 'otp-input-react';
import { Steps, Modal, Card, Input, Form, Space, Typography, Spin, Row, Col, message } from 'antd';
import { useTranslation, Button, Location } from 'soxo-bootstrap-core';
import { MailOutlined, MessageOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { Appointments } from '../../../../models';
import AppointmentDetailsCard from '../appointment-details-card/appointment-details-card';
import { useOtpTimer } from '../../../../hooks/use-otp-timer';
import { getNormaliseGender } from '../../../../utils/common.utils';

import './change-profile.scss';

const { Title, Text } = Typography;
const { Step } = Steps;

const ChangeProfile = ({ enableEmail, enableMobile, enableWhatsapp }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();

    //  Hook for OTP Timer
    const { expired: otpExpired, formatted, startFromExpiry } = useOtpTimer();

    //  for step
    const [current, setCurrent] = useState(0);

    // for loading controlll
    const [loading, setLoading] = useState(false);

    // for appointment guest details
    const [guest, setGuest] = useState({});

    const [appointmentIdInput, setAppointmentIdInput] = useState('');

    // All customers with the same email is save in existingCustomers
    const [existingCustomers, setExistingCustomers] = useState([]);

    // for selected appintment details card
    const [selectedGuest, setSelectedGuest] = useState(null);

    // OTP managing states
    const [selectedOtp, setSelectedOtp] = useState(null);
    // for OTP model
    const [isOtpModalVisible, setIsOtpModalVisible] = useState(false);
    const [otpValues, setOtpValues] = useState('');

    // Extract query parameters from URL
    const query = Location.search();
    const appointmentIdFromQuery = query.appointmentId;

    // normalize guest gender
    const guestGender = getNormaliseGender(guest?.gender);

    // selecting the contact detail based on `selectedOtp`.
    const valueMap = {
        email: selectedGuest?.email,
        mobile: selectedGuest?.mobile1,
        whatsapp: selectedGuest?.mobile1,
    };

    // By using it as a key in `valueMap`, we get the correct value dynamically.

    // `selectedOtp` is a variable that contains the chosen OTP method
    // (e.g., "email", "mobile", or "whatsapp").
    const value = valueMap[selectedOtp];

    // API for send otp to selecting contact
    const sendOtp = async (method) => {
        try {
            setLoading(true);
            const payload = {
                type: selectedOtp, // 'email' | 'sms' | 'whatsapp'
                value,
            };
            const res = await Appointments.sendOtp(payload); // create/send API

            setIsOtpModalVisible(true);
            setOtpValues('');

            if (res?.expirytime) {
                startFromExpiry(res?.expirytime);
            } else {
                message.info(res?.message);
            }
        } catch (err) {
        } finally {
            setLoading(false);
        }
    };

    //   verify otp
    const verifyOtp = async () => {
        try {
            setLoading(true);
            const payload = {
                value,
                type: selectedOtp,

                mode: 'completeprofile',
                otp: otpValues,
            };
            const res = await Appointments.userVerifyOtp(payload); // <-- create/verify API

            if (res?.success) {
                message.success(res.message);
                setIsOtpModalVisible(false);
                setCurrent(3); // move to confirmation step
            } else {
                message.error(res.message);
                setOtpValues('');
                startFromExpiry(null); // reset timer on invalid OTP
            }
            setOtpValues('');
            setLoading(false);
        } catch (err) {}
    };

    const handleNext = async () => {
        setOtpValues('');
        if (current === 0 && selectedGuest) {
            try {
                // Prepare email search body
                const emailSearch = {
                    search_by: 'email',
                    criteria: 'anywhere',
                    search_text: selectedGuest.email_address,
                    index: 'ALL',
                };

                // Prepare mobile search body
                const mobileSearch = {
                    search_by: 'mobile1',
                    criteria: 'anywhere',
                    search_text: selectedGuest.phone_numer,
                    index: 'ALL',
                };

                // Run both emaila nd phone searches in parallel
                const [emailRes, mobileRes] = await Promise.all([
                    Appointments.consolidatedSearch(emailSearch),
                    Appointments.consolidatedSearch(mobileSearch),
                ]);

                // Combine both results
                const combinedConsolidateSearchResults = [...(emailRes?.result || []), ...(mobileRes?.result || [])];

                // Remove duplicates by prereg_id
                const uniqueCustomer = combinedConsolidateSearchResults.reduce((acc, current) => {
                    if (!acc.some((item) => item.prereg_id === current.prereg_id)) {
                        acc.push(current);
                    }
                    return acc;
                }, []);

                setExistingCustomers(uniqueCustomer);
                setSelectedGuest(null);
            } catch (err) {
                console.error('Search error:', err);
                setExistingCustomers([]);
            }
        }

        // OTP step
        if (current === 2 && selectedOtp) {
            await sendOtp(selectedOtp);
            // setIsOtpModalVisible(true);
            return;
        }

        setCurrent(current + 1);
    };

    // submit otp
    const handleOtpSubmit = () => {
        verifyOtp();
    };

    useEffect(() => {
        if (appointmentIdFromQuery) {
            setAppointmentIdInput(appointmentIdFromQuery);
            loadAppointmentsDetails(appointmentIdFromQuery); //  call API immediately
        }
    }, [appointmentIdFromQuery]);

    /**
     * Loads appointment details for a given appointment ID.
     *
     * @async
     * @function loadAppointmentsDetails
     * @param {string|number} id - The appointment ID to fetch details for.
     *
     * @description
     * - If no `id` is provided, the function returns early (does nothing).
     * - Sets the `loading` state to `true` to indicate data is being fetched.
     * - Calls the backend API (`Appointments.getAppointmentRecord(id)`) to fetch the appointment record.
     * - On success: updates the `guest` state with the response data.
     *
     *
     */
    async function loadAppointmentsDetails(id) {
        if (!id) return;
        setLoading(true);
        try {
            const res = await Appointments.getAppointmentRecord(id);

            if (res?.success && res?.data) {
                setGuest(res.data);
            } else {
                message.error(res.message);
                setSelectedGuest(null);
                setGuest({}); // reset guest
            }
        } catch (error) {
            message.error('Failed to fetch appointment. Please try again.');
            setGuest({});
        }
        setLoading(false);
    }

    // Confirms a profile change request for the current guest.
    const confirmProfileChange = async () => {
        try {
            setLoading(true);

            const formBody = {
                doctorappointment_id: guest?.appointment_id,
                prereg_id: selectedGuest?.prereg_id,
                username: selectedGuest?.user_name,
            };

            const res = await Appointments.confirmProfileChange(formBody);

            if (res.success) {
                message.success(res.message);
                Location.navigate({ url: `/appointment/${guest.appointment_id}` });
            } else {
                message.warn(res?.message);
            }
        } catch (err) {
            console.error('Error in confirmProfileChange:', err);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Steps configuration for the profile change / appointment flow.
     *
     * @constant
     * @type {Array<Object>}
     *
     * @description
     *
     *
     * Steps Overview:
     *
     * 1. Step 1 — Appointment
     *    - Allows user to enter an appointment ID.
     *    - Fetches appointment details via `loadAppointmentsDetails`.
     *    - Displays the appointment details inside `AppointmentDetailsCard`.
     *    - Shows a loading spinner (`Spin`) while fetching data.
     *
     * 2. Step 2 — Old Appointment
     *    - Lets the user search and select an existing guest profile
     *      (from `existingCustomers` array).
     *    - Displays results as `AppointmentDetailsCard` components in a grid.
     *    - Highlights the selected guest (comparison by `prereg_id`).
     *
     * 3. Step 3 — OTP Verification
     *    - User chooses their preferred OTP method (Mobile, Email, WhatsApp).
     *    - Available methods depend on flags (`enableMobile`, `enableEmail`, `enableWhatsapp`).
     *    - Each option is shown as a card with an icon + label.
     *    - Clicking a card sets `selectedOtp` to the chosen method.
     *
     * 4. Step 4 — Confirmation
     *    - Displays the selected guest profile for final review.
     *    - Prompts the user to confirm profile change.
     *    - Buttons for "Yes" and "No" (currently commented out).
     *
     */

    const steps = [
        {
            title: 'Step 1',
            description: 'Appointment',
            preview: 'Find your appointment',
            content: (
                <div>
                    <Title className="guest-headings" level={5}>
                        {t('Appointment Information')}
                    </Title>
                    <Text type="secondary">{t('This section shows your appointment details')}</Text>

                    <Card style={{ marginTop: 16 }}>
                        <Form form={form} layout="vertical">
                            <Form.Item label="Appointment ID" name="appointmentId">
                                <Space wrap>
                                    <Input
                                        style={{ minWidth: 200 }}
                                        value={appointmentIdInput}
                                        disabled={appointmentIdFromQuery}
                                        onChange={(e) => setAppointmentIdInput(e.target.value)}
                                        placeholder="Enter Appointment ID"
                                    />
                                    {appointmentIdFromQuery ? null : (
                                        <Button
                                            type="primary"
                                            onClick={() => loadAppointmentsDetails(appointmentIdInput)}
                                            disabled={!appointmentIdInput?.trim()} // disable if input is empty or spaces
                                        >
                                            Load Appointment
                                        </Button>
                                    )}
                                </Space>
                            </Form.Item>
                        </Form>
                    </Card>

                    {/* Spinner */}
                    {loading && <Spin style={{ marginTop: 16 }} />}

                    {/* Appointment Details */}
                    {guest.appointment_id ? (
                        <AppointmentDetailsCard
                            guest={guest}
                            onSelect={setSelectedGuest}
                            selected={selectedGuest?.appointment_id === guest?.appointment_id}
                        />
                    ) : null}
                </div>
            ),
        },
        {
            title: 'Step 2',
            description: 'Old Appointment',
            preview: 'Locate old appointment',

            content: (
                <Card>
                    <Title level={5}>{t('Search and Select Guest Profile')}</Title>
                    <Text type="secondary">{t('Find Guest Profile Using Email or Phone')}</Text>

                    {loading && <Spin style={{ marginTop: 16 }} />}
                    {!loading && (
                        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                            {existingCustomers
                                .filter((appt) => {
                                    const apptGender = getNormaliseGender(appt?.gender);
                                    // Hide same prereg
                                    const isDifferentPrereg = appt?.prereg_id !== guest?.prereg_id;
                                    // only keep if both match
                                    const isSameGender = guestGender && apptGender && guestGender === apptGender;

                                    return isSameGender && isDifferentPrereg;
                                })
                                .map((appt) => (
                                    <Col xs={24} md={24} key={appt.appointment_id}>
                                        <AppointmentDetailsCard
                                            guest={appt}
                                            onSelect={setSelectedGuest}
                                            selected={selectedGuest?.prereg_id === appt.prereg_id}
                                        />
                                    </Col>
                                ))}
                        </Row>
                    )}
                </Card>
            ),
        },
        {
            title: 'Step 3',
            description: 'OTP Verification',
            preview: 'Verify using OTP',
            content: (
                <Card>
                    <Title level={5}>{t('Select Your Preferred Method For OTP Verification.')}</Title>
                    <Text type="secondary">{t("Select how you'd like to receive the OTP. We'll send it to your chosen method")}</Text>
                    <Row gutter={[16, 16]} style={{ marginTop: 24, textAlign: 'center' }}>
                        {[
                            enableMobile && {
                                type: 'mobile',
                                label: selectedGuest?.mobile1,
                                icon: <MessageOutlined style={{ color: '#1890ff', fontSize: 28 }} />,
                            },
                            enableEmail && {
                                type: 'email',
                                label: selectedGuest?.email,
                                icon: <MailOutlined style={{ color: '#f5222d', fontSize: 28 }} />,
                            },
                            enableWhatsapp && {
                                type: 'whatsapp',
                                label: selectedGuest?.mobile1,
                                icon: <WhatsAppOutlined style={{ color: '#25D366', fontSize: 28 }} />,
                            },
                        ]
                            // remove falsy values
                            .filter(Boolean)
                            .map((method) => (
                                <Col xs={24} md={8} key={method.type}>
                                    <Card
                                        hoverable
                                        bordered
                                        className={`otp-method-card ${selectedOtp === method.type ? 'selected' : ''}`}
                                        onClick={() => setSelectedOtp(method.type)}
                                    >
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ marginBottom: 12 }}>{method.icon}</div>
                                            <Text strong>{method.label}</Text>
                                        </div>
                                    </Card>
                                </Col>
                            ))}
                    </Row>
                </Card>
            ),
        },
        {
            title: 'Step 4',
            description: 'Confirmation',
            preview: 'Confirm details to save',
            content: (
                <Card>
                    <Title level={5}>{t('Confirm Profile Change.')}</Title>
                    <Text type="secondary">{t('Finalize your profile change')}</Text>
                    <AppointmentDetailsCard guest={selectedGuest} />
                </Card>
            ),
        },
    ];

    return (
        <Card>
            <Row gutter={[24, 24]}>
                {/* Sidebar Steps */}
                <Col xs={24} md={6}>
                    <Steps
                        direction={window.innerWidth < 768 ? 'horizontal' : 'vertical'} // switch direction for small screens
                        current={current}
                        onChange={(value) => setCurrent(value)}
                    >
                        {steps.map((item, index) => (
                            <Step
                                key={index}
                                title={
                                    <div>
                                        <div className="step-item-title">{item.title}</div>
                                        <div className="step-item-description">{item.description}</div>
                                        <div className="step-item-preview">{item.preview}</div>
                                    </div>
                                }
                            />
                        ))}
                    </Steps>
                </Col>

                {/* Step Content */}
                <Col xs={24} md={18}>
                    {steps[current].content}

                    <div style={{ marginTop: 24, display: 'flex', gap: '8px' }}>
                        {current > 0 && (
                            <Button type="default" onClick={() => setCurrent(current - 1)} style={{ marginRight: 8 }}>
                                Previous
                            </Button>
                        )}
                        {current < steps.length - 1 && (
                            <Button
                                type="primary"
                                disabled={
                                    ((current === 0 || current === 1) && !selectedGuest) || (current === 2 && !selectedOtp) // disable if no method selected
                                }
                                onClick={handleNext}
                            >
                                Next
                            </Button>
                        )}
                        {current === steps.length - 1 && (
                            <Button type="primary" onClick={confirmProfileChange}>
                                Continue
                            </Button>
                        )}
                    </div>
                </Col>
            </Row>

            <Modal
                title={t('OTP Verification')}
                open={isOtpModalVisible}
                onCancel={() => {
                    setIsOtpModalVisible(false);
                    setOtpValues('');
                    startFromExpiry(null); // reset timer when closing modal
                }}
                footer={null}
                centered
                maskClosable={false} // Prevent closing on outside click  and keyboard ESC
                keyboard={false}
            >
                <p>
                    {t('Enter the verification code we sent to')}{' '}
                    <strong>{selectedOtp === 'email' ? selectedGuest?.email: selectedGuest?.mobile1}</strong>
                </p>

                <OTPInput value={otpValues} onChange={setOtpValues} autoFocus OTPLength={6} disabled={otpExpired} />

                {/* Timer below OTP */}
                <div className="otp-timer">{otpExpired ? null : <span>{formatted}</span>}</div>

                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                        <Text type="secondary">Do Not Send OTP?</Text>
                        {/* Resend Button on left */}
                        <Button
                            type="link"
                            onClick={() => {
                                //  Call resend OTP API here
                                // setIsOtpModalVisible(false);
                                sendOtp();

                                setOtpValues('');
                            }}
                            disabled={!otpExpired} //  only active after expiry
                        >
                            {t('Resend OTP')}
                        </Button>
                    </div>
                    <Button
                        type="primary"
                        onClick={() => {
                            // verify success
                            handleOtpSubmit();

                            // setIsOtpModalVisible(false);
                            // setCurrent(3); // jump to confirmation step
                        }}
                        disabled={otpExpired || otpValues.length !== 6} //  block expired
                    >
                        {t('Submit')}
                    </Button>
                </div>
            </Modal>
        </Card>
    );
};

export default ChangeProfile;
